import React from "react";

import opacity from "hex-color-opacity";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useElementSize } from "usehooks-ts";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import { formatBalance } from "../PerformanceGroups/Shared/helpers";
import Text from "@src/sharedComponents/Typography/Text";

import CustomDot from "./CustomDot";
import graphConfig from "./graphConfig";
import GraphTooltip from "./GraphTooltip";

import styles from "./ProjectionGraph.module.css";

export interface GraphDataPoint {
  date: string; // yyyy-MM-dd
  range: [number, number]; // [min, max]
  value: number;
}

const formatTickForNoData = (tickValue: number): string => {
  if (tickValue === 0) {
    return "$0";
  }

  return "$--";
};

interface Props {
  graphHeight?: number;
  xAxisTickCount?: number;
  data: GraphDataPoint[];
}

const FinancialPlansProjectionGraph: React.FC<Props> = ({
  graphHeight,
  xAxisTickCount,
  data,
}: Props) => {
  const {
    color: { $chartSegmentOne, $border, $textSubtle },
  } = useTheme();

  const areaFillColor = opacity(
    $chartSegmentOne,
    graphConfig.areaFillColorOpacity
  );

  const [chartRef, { width }] = useElementSize();

  return (
    <div className={styles.graphContainer} ref={chartRef}>
      <ResponsiveContainer width="100%" height={graphHeight}>
        <ComposedChart
          width={width}
          data={data}
          margin={{
            top: graphConfig.marginTop,
            right: graphConfig.marginRight,
            left: graphConfig.marginLeft,
            bottom: graphConfig.marginBottom,
          }}
        >
          <CartesianGrid
            vertical={false}
            stroke={$border}
            strokeDasharray="0"
            strokeWidth={graphConfig.gridStrokeWidth}
          />

          <XAxis
            style={{ fontSize: graphConfig.axisFontsize }}
            height={graphConfig.XAxisHeight}
            dataKey="date"
            stroke={$textSubtle}
            dy={graphConfig.dy}
            tickLine={false}
            tick={xAxisTickCount === 2 ? false : true}
            tickFormatter={(date: string) => date.substring(0, 4)}
            tickCount={7}
            interval={Math.round(data.length / 6) - 2}
          />

          <YAxis
            style={{ fontSize: graphConfig.axisFontsize }}
            width={graphConfig.YAxisWidth}
            tickFormatter={(balance: number) => {
              if (data.length > 0) {
                return formatBalance(balance);
              }

              return formatTickForNoData(balance);
            }}
            tickCount={graphConfig.yTickCount}
            stroke={$textSubtle}
            axisLine={false}
            tickLine={false}
            tickMargin={graphConfig.tickMargin}
            dx={graphConfig.dx}
            domain={data.length > 0 ? undefined : [0, 1_000_000]}
            orientation="right"
          />

          <Line
            dataKey="value"
            type="natural"
            isAnimationActive={false}
            strokeWidth={graphConfig.strokeWidth}
            stroke={$chartSegmentOne}
            connectNulls
            dot={false}
            activeDot={(props) => (
              <CustomDot
                props={{
                  ...props,
                  graphWidth: width,
                  graphHeight: graphHeight,
                }}
              />
            )}
          />

          <Area
            dataKey="range"
            type="monotone"
            stroke="none"
            fill={areaFillColor}
            dot={false}
            activeDot={false}
            connectNulls
          />

          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: "none" }}
            content={(props) => (
              <GraphTooltip
                props={{
                  ...props,
                }}
              />
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {xAxisTickCount === 2 && (
        <div className={styles.xaxisTicks}>
          <Text variant={"body"} color={"textSubtle"}>
            {" "}
            {data[0].date}{" "}
          </Text>

          <Text variant={"body"} color={"textSubtle"}>
            {" "}
            {data[data.length - 1].date}{" "}
          </Text>
        </div>
      )}
    </div>
  );
};

export default FinancialPlansProjectionGraph;
